import {
  Flex,
  HStack,
  useRadioGroup,
  Text,
  SimpleGrid
} from '@chakra-ui/react';
import { useCrucibles } from 'hooks/query/useCrucibles';
import { useWalletAssets } from 'hooks/query/useWalletAssets';
import { useWeb3React } from 'hooks/web3';
import { BiTransfer } from 'react-icons/bi';
import { CgSoftwareDownload, CgSoftwareUpload } from 'react-icons/cg';
import { MdWrapText } from 'react-icons/md';
import { useConfig } from 'store/config';
import RadioCard from './RadioCard';

type Props = {
  value: number;
  onChange: (index: number) => void;
};

const CustomAssetRadio: React.FC<Props> = ({ value, onChange }) => {
  const { chainId = 1 } = useWeb3React();
  const { data: crucibles } = useCrucibles();
  const { data: walletAssets } = useWalletAssets();
  const { wrappableTokens } = useConfig(chainId).config;

  const options = [
    {
      value: 0,
      label: 'Deposit',
      icon: <CgSoftwareDownload />,
      description:
        'Transfer any supported ERC20 token into your Crucible from your wallet.'
    },
    {
      value: 1,
      label: 'Withdraw',
      icon: <CgSoftwareUpload />,
      description:
        'Transfer out any supported ERC20 token from your Crucible to your wallet.'
    },
    {
      value: 2,
      label: 'Transfer',
      icon: <BiTransfer />,
      description: 'Transfer any supported ERC20 token to another Crucible.',
      disabled: crucibles && crucibles.length === 1
    },
    {
      value: 3,
      label: 'Wrap/Unwrap',
      icon: <MdWrapText />,
      description:
        'Wrap or unwrap ERC20 tokens in your wallet. If the token is in your Crucible, you need to withdraw it to your wallet first.',
      disabled: !walletAssets || !wrappableTokens
    }
  ];

  const { getRootProps, getRadioProps } = useRadioGroup({
    value,
    onChange: (nextValue: string) => onChange(Number(nextValue)),
    name: 'assetTransfer'
  });

  const group = getRootProps();

  return (
    <SimpleGrid {...group} spacing={[3, 3, 8]} alignItems='stretch' columns={2}>
      {options.map(({ label, value, description, icon, disabled }) => {
        const radio = getRadioProps({ value });
        return (
          <RadioCard key={label} {...radio} isDisabled={disabled}>
            <Flex
              p={[1, 2, 6]}
              flexDir='column'
              justifyContent='center'
              alignItems='center'
              color={disabled ? 'gray.600' : 'inherit'}
            >
              <HStack fontSize={['xs', 'sm', 'xl']}>
                {icon}
                <Text fontWeight='500'>{label}</Text>
              </HStack>
              <Text
                mt={3}
                color={disabled ? 'gray.600' : 'gray.200'}
                display={['none', null, 'block']}
              >
                {description}
              </Text>
            </Flex>
          </RadioCard>
        );
      })}
    </SimpleGrid>
  );
};

export default CustomAssetRadio;
